export const CATALOG_MODES = ["FIXED_SERVICE", "INSPECTION_FIRST"];
export const CATALOG_SOURCE_TYPES = [
  "CATEGORY",
  "SUBCATEGORY",
  "MASTER_SERVICE",
  "CHILD_SERVICE",
  "BRAND",
];
export const MAX_CATALOG_DEPTH = 5;

const text = (value) => String(value || "").trim();

const canonicalMetadata = (node = {}) => {
  const source = node.source || {};
  const canonical = node.canonicalSource || node.canonical || {};
  return {
    canonicalLabel: text(
      node.canonicalLabel || node.sourceLabel || source.label || source.title
      || canonical.label || canonical.title,
    ),
    canonicalImageUrl: text(
      node.canonicalImageUrl || node.sourceImageUrl || source.imageUrl || source.image
      || canonical.imageUrl || canonical.image,
    ),
  };
};

export const emptyCatalogNode = (depth = 1, kind = "NAVIGATION") => ({
  nodeId: null,
  kind,
  sourceType: CATALOG_SOURCE_TYPES[Math.min(depth - 1, CATALOG_SOURCE_TYPES.length - 1)],
  sourceId: null,
  canonicalLabel: "",
  canonicalImageUrl: "",
  label: "",
  subtext: "",
  imageOverrideUrl: "",
  offeringId: "",
  children: [],
});

export const normalizeCatalogNode = (node = {}) => ({
  nodeId: node.nodeId || null,
  kind: node.kind === "OFFERING" ? "OFFERING" : "NAVIGATION",
  sourceType: node.source?.type || node.sourceType || "CATEGORY",
  sourceId: node.source?.id ?? node.sourceId ?? null,
  ...canonicalMetadata(node),
  label: node.label || "",
  subtext: node.subtext || "",
  imageOverrideUrl: node.imageOverrideUrl || "",
  offeringId: node.offeringId || "",
  children: (node.children || []).map(normalizeCatalogNode),
});

export const normalizeCatalogRoots = (roots = []) => roots.map(normalizeCatalogNode);

export const toCatalogNodePayload = (node, displayOrder = 0) => {
  const payload = {
    ...(node.nodeId ? { nodeId: node.nodeId } : {}),
    kind: node.kind,
    source: { type: node.sourceType, id: Number(node.sourceId) },
    label: text(node.label),
    ...(text(node.subtext) ? { subtext: text(node.subtext) } : {}),
    ...(text(node.imageOverrideUrl) ? { imageOverrideUrl: text(node.imageOverrideUrl) } : {}),
    displayOrder,
    ...(node.kind === "OFFERING" ? { offeringId: text(node.offeringId) } : {}),
    children: node.kind === "OFFERING"
      ? []
      : (node.children || []).map((child, index) => toCatalogNodePayload(child, index)),
  };
  return payload;
};

export const toCatalogDraftPayload = ({ mode, expectedVersion, roots }) => ({
  mode,
  expectedVersion,
  roots: roots.map((node, index) => toCatalogNodePayload(node, index)),
});

const isPlainText = (value) => !/[<>]/.test(value) && !Array.from(value).some((character) => {
  const code = character.charCodeAt(0);
  return (code < 32 && ![9, 10, 13].includes(code)) || code === 127;
});
export const isSafeImageReference = (value) => {
  const result = text(value);
  if (!result) return true;
  if (result.length > 1000 || /[<>]/.test(result) || Array.from(result).some((character) => {
    const code = character.charCodeAt(0);
    return code < 32 || code === 127;
  })) return false;

  const safePath = /^\/?[a-zA-Z0-9][a-zA-Z0-9_./-]*$/.test(result)
    && !result.includes("..") && !result.includes("\\");
  let safeHttpUrl = false;
  try {
    const parsed = new URL(result);
    safeHttpUrl = ["http:", "https:"].includes(parsed.protocol)
      && !parsed.username && !parsed.password;
  } catch {
    safeHttpUrl = false;
  }
  return safeHttpUrl || safePath;
};

export const validateCatalogTree = (roots, availableOfferingIds = null) => {
  const errors = [];
  const offeringIds = new Set();
  let offeringCount = 0;

  if (!Array.isArray(roots) || roots.length === 0) errors.push("Add at least one catalog node.");

  const visit = (nodes, depth, breadcrumb) => {
    if (depth > MAX_CATALOG_DEPTH) {
      errors.push(`${breadcrumb || "Catalog"} exceeds the five-level limit.`);
      return;
    }
    nodes.forEach((node, index) => {
      const place = `${breadcrumb || "Root"} ${index + 1}`;
      const label = text(node.label);
      if (!CATALOG_SOURCE_TYPES.includes(node.sourceType) || !Number.isSafeInteger(Number(node.sourceId)) || Number(node.sourceId) <= 0) {
        errors.push(`${place} must link to a canonical source.`);
      }
      if (!label || label.length > 160 || !isPlainText(label)) errors.push(`${place} needs a plain-English label of at most 160 characters.`);
      if (text(node.subtext).length > 500 || !isPlainText(text(node.subtext))) errors.push(`${place} subtext must be plain text of at most 500 characters.`);
      if (!isSafeImageReference(node.imageOverrideUrl)) errors.push(`${place} image override must be a safe HTTP(S) URL or media path of at most 1000 characters.`);

      if (node.kind === "OFFERING") {
        offeringCount += 1;
        if (!text(node.offeringId)) errors.push(`${place} must select an offering.`);
        if (text(node.offeringId) && availableOfferingIds && !availableOfferingIds.has(node.offeringId)) errors.push(`${place} links an unavailable offering.`);
        if (offeringIds.has(node.offeringId)) errors.push(`${place} repeats offering ${node.offeringId}.`);
        offeringIds.add(node.offeringId);
        if ((node.children || []).length) errors.push(`${place} is an offering and cannot contain children.`);
      } else {
        if (depth === MAX_CATALOG_DEPTH) errors.push(`${place} must be an offering at level five.`);
        if (!(node.children || []).length) errors.push(`${place} needs at least one child or must be changed to an offering.`);
        visit(node.children || [], depth + 1, label || place);
      }
    });
  };

  visit(roots || [], 1, "");
  if (!offeringCount) errors.push("Add at least one offering to the catalog.");
  return [...new Set(errors)];
};

export const optionId = (option) => Number(option?.id ?? option?.sourceId ?? option?.brandId ?? option?.source?.id ?? option?.value);
export const optionLabel = (option) => option?.label || option?.title || option?.name || option?.source?.label || option?.source?.title || `Item #${optionId(option)}`;
export const optionImage = (option) => option?.imageUrl || option?.image || option?.iconUrl || option?.source?.imageUrl || option?.source?.image || "";

export const catalogBreadcrumbText = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => typeof item === "string" ? item : item?.label || item?.title).filter(Boolean).join(" › ");
  }
  return text(value);
};
